import { LitElement, css, html, nothing } from 'lit';
import { parseMapLink, type ReleaseManifest } from '@ka/contracts';
import { SITE_TITLE } from '@ka/contracts/canon';
import { readProgress, resetProgress, shared, spiderSvg, webCorner } from '@ka/ui';
import { hasNewerRelease, loadRelease, type LoadResult } from './data.ts';
import { rememberInvite, storedInvite } from './family-auth.ts';
import { loadKid, startProgressSync, currentKid } from './kid.ts';
import * as pages from './pages.ts';
import './parents.ts';
import './fun.ts';
import './redstone-game.ts';
import './skin-studio.ts';
import './minecart-game.ts';
import './expedition-game.ts';
import { setEmitter, startRouter, type Route } from './router.ts';
import './map-view.ts';

const NAV = [
  ['/episodes', 'Episodes'],
  ['/map', 'Map'],
  ['/characters', 'Characters'],
  ['/lore', 'Lore'],
  ['/fun', 'Fun'],
  ['/about', 'About'],
] as const;

export class KaApp extends LitElement {
  static override properties = {
    data: { state: true },
    route: { state: true },
    newer: { state: true },
    tick: { state: true },
    menuOpen: { state: true },
  };
  declare data: LoadResult | null;
  declare route: Route | null;
  declare newer: boolean;
  declare tick: number;
  declare menuOpen: boolean;

  static override styles = [
    shared,
    css`
      .skip {
        position: absolute;
        left: -999px;
      }
      .skip:focus {
        left: 1rem;
        top: 1rem;
        z-index: 10;
      }
      header {
        background: var(--ka-stone-800);
        border-bottom: 3px solid var(--ka-edge);
        box-shadow: var(--ka-bevel);
      }
      .wrap {
        max-width: var(--ka-max);
        margin: 0 auto;
        padding-inline: 1rem; /* main.wrap sets its own top and bottom space */
      }
      header .wrap {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        align-content: flex-start;
        justify-content: space-between;
        gap: 0.5rem;
        min-height: 4rem;
      }
      .brand {
        display: inline-flex;
        align-items: center;
        min-height: 4rem; /* the top row keeps this height when the menu opens below it */
        gap: 0.5rem;
        font: 600 1.2rem var(--ka-font-display);
        color: var(--ka-parchment);
        text-decoration: none;
      }
      .brand svg {
        color: var(--ka-red);
      }
      nav ul {
        display: flex;
        flex-wrap: wrap;
        gap: 0.25rem;
        list-style: none;
        margin: 0;
        padding: 0;
      }
      nav a {
        display: inline-flex;
        align-items: center;
        min-height: 44px;
        padding: 0 0.75rem;
        color: var(--ka-parchment);
        text-decoration: none;
        font-family: var(--ka-font-display);
      }
      nav a[aria-current='page'] {
        color: var(--ka-gold);
        box-shadow: inset 0 -3px 0 var(--ka-red);
      }
      .menu-btn {
        display: none;
      }
      /* Phones: the links fold into a Menu button so the header stays one row. */
      @media (max-width: 760px) {
        .menu-btn {
          display: inline-flex;
          justify-content: center;
          min-width: 6.5rem; /* same size for Menu and Close */
        }
        nav {
          display: none;
          flex-basis: 100%;
          padding-bottom: 0.5rem;
        }
        nav.open {
          display: block;
        }
        nav ul {
          flex-direction: column;
        }
        nav a {
          display: flex;
          width: 100%;
          box-sizing: border-box;
        }
        nav a[aria-current='page'] {
          box-shadow: inset 3px 0 0 var(--ka-red);
        }
      }
      /* Fill the window so the footer sits at the bottom on short pages. */
      :host {
        display: flex;
        flex-direction: column;
        min-height: 100vh;
        min-height: 100dvh;
      }
      main {
        flex: 1 0 auto;
        width: 100%;
        box-sizing: border-box;
        padding-block: 2rem 4rem;
        display: grid;
        align-content: start;
        gap: 2rem;
      }
      footer {
        border-top: 3px solid var(--ka-edge);
        background: var(--ka-stone-800);
        padding-block: 1.5rem;
        font-size: 0.9rem;
      }
      .banner {
        padding: 0.6rem 1rem;
        text-align: center;
        font: 600 1rem var(--ka-font-display);
        border-bottom: 3px solid var(--ka-edge);
      }
      .banner.draft {
        background: var(--ka-red);
        color: var(--ka-white);
      }
      .banner.fixture {
        background: var(--ka-gold);
        color: var(--ka-ink-on-gold);
      }
      .banner.newer {
        background: var(--ka-foliage-dim);
      }
      .hero {
        padding: 2rem 0;
      }
      .kicker {
        font-family: var(--ka-font-display);
        color: var(--ka-foliage);
        margin: 0 0 0.25rem;
      }
      .tagline {
        font: 600 1.25rem var(--ka-font-display);
        color: var(--ka-gold);
        margin: 0 0 1rem;
      }
      .lede {
        font-size: 1.2rem;
        max-width: 60ch;
      }
      .ctas {
        display: flex;
        flex-wrap: wrap;
        gap: 1rem;
      }
      .plain {
        list-style: none;
        padding: 0;
        margin: 0;
      }
      .books {
        list-style: none;
        padding: 0;
        margin: 0;
        display: grid;
        gap: 1.25rem;
        grid-template-columns: repeat(auto-fill, minmax(min(100%, 15rem), 1fr));
      }
      .book a {
        display: grid;
        gap: 0.25rem;
        padding: 0.75rem 0.75rem 1rem 1.25rem;
        background: linear-gradient(90deg, var(--ka-leather-spine) 0 10px, var(--ka-leather) 10px);
        border: 2px solid var(--ka-edge);
        box-shadow: var(--ka-lift);
        color: var(--ka-parchment);
        text-decoration: none;
        transition: transform 0.15s;
      }
      .book a:hover,
      .book a:focus-visible {
        transform: translateY(-3px);
      }
      .book img,
      .book-cover {
        width: 100%;
        aspect-ratio: 16 / 9;
        object-fit: cover;
        border: 2px solid var(--ka-edge);
        background: var(--ka-stone-900);
        display: grid;
        place-items: center;
        color: var(--ka-red);
      }
      .book-num {
        font: 600 0.85rem var(--ka-font-display);
        color: var(--ka-gold);
      }
      .book-title {
        font: 600 1.15rem var(--ka-font-display);
      }
      .char {
        border-left: 6px solid var(--ka-foliage);
      }
      /* The whole card opens the character: the name's link is stretched over the card. */
      li.char {
        cursor: pointer;
        transition: transform 0.15s;
      }
      li.char h3 a::after {
        content: '';
        position: absolute;
        inset: 0;
      }
      li.char:hover,
      li.char:focus-within {
        transform: translateY(-3px);
        box-shadow: var(--ka-lift);
      }
      li.char:focus-within {
        outline: 3px solid var(--ka-focus);
      }
      li.char h3 a:focus-visible {
        outline: none;
      }
      .char-media {
        display: grid;
        gap: 1rem;
        align-content: start;
      }
      img.art {
        width: 100%;
        aspect-ratio: 3 / 4;
        object-fit: cover;
        border: 2px solid var(--ka-edge);
        background: var(--ka-stone-900);
      }
      .accent-red.char,
      .accent-red .tag {
        border-color: var(--ka-red);
      }
      .accent-purple.char,
      .accent-purple .tag {
        border-color: var(--ka-purple);
      }
      .two-col {
        display: grid;
        gap: 1.5rem;
        grid-template-columns: minmax(0, 2fr) minmax(16rem, 1fr);
        align-items: start;
      }
      .two-col.char-page {
        grid-template-columns: minmax(14rem, 1fr) minmax(0, 2fr);
      }
      @media (max-width: 800px) {
        .two-col,
        .two-col.char-page {
          grid-template-columns: 1fr;
        }
      }
      .placeholder {
        text-align: center;
        padding: 1rem;
        color: var(--ka-parchment-dim);
      }
      .chapters button.link,
      button.link {
        all: unset;
        cursor: pointer;
        color: var(--ka-gold);
        text-decoration: underline;
        min-height: 44px;
        min-width: 44px;
      }
      button.link:focus-visible {
        outline: 3px solid var(--ka-focus);
      }
      .scroll {
        background: linear-gradient(var(--ka-parchment), var(--ka-scroll-shade));
        color: var(--ka-scroll-ink);
        max-width: 70ch;
      }
      .scroll a {
        color: var(--ka-scroll-link);
      }
      .scroll .tag {
        color: var(--ka-scroll-ink);
      }
      .hidden-spider {
        justify-self: end;
        opacity: 0.6;
      }
      .web {
        position: absolute;
        top: 0;
        right: 0;
        color: var(--ka-parchment);
      }
      .state {
        text-align: center;
        padding: 4rem 1rem;
      }
    `,
  ];

  constructor() {
    super();
    this.data = null;
    this.route = null;
    this.newer = false;
    this.tick = 0;
    this.menuOpen = false;
  }

  override connectedCallback() {
    super.connectedCallback();
    startProgressSync();
    // Wait for the kid profile so its progress is in place before the hunt renders.
    void Promise.all([loadRelease(), loadKid()]).then(([d]) => (this.data = d));
    setEmitter(startRouter((r) => this.onRoute(r)));
    window.addEventListener('ka-secret-progress', () => this.tick++);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && this.data?.kind === 'release') {
        void hasNewerRelease(this.data.siteRevision).then((n) => (this.newer = n));
      }
    });
  }

  private onRoute(r: Route) {
    if (r.query.has('invite')) {
      // The card's QR: keep the code for sign-up in this tab, then drop it from the address bar.
      rememberInvite(r.query.get('invite'));
      history.replaceState(null, '', location.pathname);
    }
    const changedPage = this.route?.name !== r.name || this.route?.params.slug !== r.params.slug;
    this.route = r;
    if (changedPage) {
      window.scrollTo(0, 0);
      // Move focus to the new page heading for screen reader and keyboard users.
      void this.updateComplete.then(() =>
        this.renderRoot.querySelector<HTMLElement>('main h1')?.focus(),
      );
    }
  }

  private secretPanel(m: ReleaseManifest) {
    const s = m.secrets[0];
    if (!s) return nothing;
    const p = readProgress(s.progressVersion);
    const found = s.puzzle.symbolIds.filter((id) => p.found.includes(id));
    const done = found.length === s.puzzle.symbolIds.length;
    const reward = m.lore.find((l) => l.id === s.rewardLoreId);
    return html`<section class="panel" aria-live="polite">
      ${webCorner}
      <h2>${spiderSvg(20)} ${s.title}</h2>
      <p>Spiders found: ${found.length} of ${s.puzzle.symbolIds.length}</p>
      ${
        done && reward
          ? html`<article class="panel scroll">
              <h3>${reward.title}</h3>
              <ka-markdown .source=${reward.body}></ka-markdown>
            </article>`
          : html`<details>
              <summary>Need a hint?</summary>
              <p>${s.puzzle.hint}</p>
            </details>`
      }
      <p>
        <button class="btn" @click=${() => resetProgress(s.progressVersion)}>
          Reset spider hunt
        </button>
      </p>
      <p class="muted">
        ${
          currentKid()
            ? `Progress is saved to ${currentKid()!.nickname}'s profile.`
            : 'Progress is saved only in this browser.'
        }
      </p>
    </section>`;
  }

  private page(): pages.Page | null {
    const d = this.data;
    const r = this.route;
    if (!d || !r) return null;
    if (d.kind === 'error')
      return { title: 'Error', body: html`<p class="panel state" role="alert">${d.message}</p>` };
    const m = d.kind === 'release' ? d.manifest : null;
    if (r.name === 'home') return pages.home(m);
    if (r.name === 'about') return pages.about(m);
    if (r.name === 'parents') return pages.parents(!!storedInvite());
    if (r.name === 'parentsSignIn' || r.name === 'parentsSignUp')
      return {
        title: r.name === 'parentsSignUp' ? 'Create a family account' : 'Parent sign-in',
        body: html`<ka-parent-auth
          start=${r.name === 'parentsSignUp' ? 'signUp' : 'signIn'}
        ></ka-parent-auth>`,
      };
    if (r.name === 'fun') return { title: 'Fun', body: html`<ka-fun-hub></ka-fun-hub>` };
    if (r.name === 'funCharacter')
      return { title: 'My character', body: html`<ka-fun-character></ka-fun-character>` };
    if (r.name === 'funSkins')
      return { title: 'Skin Studio', body: html`<ka-skin-studio></ka-skin-studio>` };
    if (r.name === 'funRedstone')
      return {
        title: 'Redstone Relay',
        body: html`<ka-redstone .levelId=${r.query.get('level') ?? ''}></ka-redstone>`,
      };
    if (r.name === 'funBlueprints') {
      void import('./blueprint-game.ts');
      return {
        title: 'Build Blueprint',
        body: html`<ka-blueprints
          .levelId=${r.query.get('level') ?? ''}
          .free=${r.query.has('free')}
          .plotId=${r.query.get('plot') ?? ''}
        ></ka-blueprints>`,
      };
    }
    if (r.name === 'funMinecarts')
      return {
        title: 'Minecart Switchyard',
        body: html`<ka-minecarts .levelId=${r.query.get('level') ?? ''}></ka-minecarts>`,
      };
    if (r.name === 'funExpedition')
      return {
        title: 'Expedition Loadout',
        body: html`<ka-expedition .adventureId=${r.query.get('adventure') ?? ''}></ka-expedition>`,
      };
    if (r.name === 'family') return { title: 'Your family', body: html`<ka-family></ka-family>` };
    if (r.name === 'pair') {
      const code = r.query.get('code') ?? '';
      // Keep the one-time code out of history once read.
      if (code) history.replaceState(null, '', '/pair');
      return {
        title: 'Pair this device',
        body: html`<ka-pair
          .code=${code}
          .version=${m?.secrets[0]?.progressVersion ?? 1}
        ></ka-pair>`,
      };
    }
    if (!m) {
      // Nothing published yet: keep the route's own heading instead of silently showing home.
      const label = NAV.find(([href]) => location.pathname.startsWith(href))?.[1];
      return label ? pages.comingSoon(label) : pages.notFound();
    }
    switch (r.name) {
      case 'episodes':
        return pages.episodes(m);
      case 'episode':
        return pages.episode(m, r);
      case 'map':
        return {
          title: 'Map',
          body: html`<ka-map-view .manifest=${m} .link=${parseMapLink(r.query)}></ka-map-view>`,
        };
      case 'characters':
        return pages.characters(m);
      case 'character':
        return pages.character(m, r);
      case 'lore':
        return pages.lore(m, new Set(m.secrets.map((s) => s.rewardLoreId)), this.secretPanel(m));
      case 'loreEntry':
        return pages.loreEntry(m, r);
      default:
        return pages.notFound();
    }
  }

  override updated() {
    const p = this.page();
    if (p) document.title = p.title === SITE_TITLE ? p.title : `${p.title} · ${SITE_TITLE}`;
    this.renderRoot.querySelector('main h1')?.setAttribute('tabindex', '-1');
  }

  override render() {
    const m = this.data?.kind === 'release' ? this.data.manifest : null;
    const path = location.pathname;
    const p = this.page();
    return html`
      <a class="skip btn" href="#main">Skip to content</a>
      ${m?.draft ? html`<div class="banner draft" role="status">DRAFT PREVIEW — not published</div>` : nothing}
      ${m?.fixture ? html`<div class="banner fixture" role="status">FIXTURE DATA — sample content for development, not real episodes</div>` : nothing}
      ${
        this.newer
          ? html`<div class="banner newer" role="status">
              A new adventure is available!
              <button class="btn" @click=${() => location.reload()}>Refresh</button>
            </div>`
          : nothing
      }
      <header>
        <div class="wrap">
          <a class="brand" href="/">${spiderSvg(28)} King Arachnid</a>
          <button
            class="btn menu-btn"
            aria-expanded=${this.menuOpen ? 'true' : 'false'}
            aria-controls="main-nav"
            @click=${() => (this.menuOpen = !this.menuOpen)}
          >
            ${this.menuOpen ? 'Close' : 'Menu'}
          </button>
          <nav
            id="main-nav"
            class=${this.menuOpen ? 'open' : ''}
            aria-label="Main"
            @click=${() => (this.menuOpen = false)}
          >
            <ul>
              ${NAV.map(([href, label]) => html`<li><a href=${href} aria-current=${path.startsWith(href) ? 'page' : 'false'}>${label}</a></li>`)}
            </ul>
          </nav>
        </div>
      </header>
      <main id="main" class="wrap">
        ${p ? p.body : html`<p class="state" role="status">Loading…</p>`}
      </main>
      <footer>
        <div class="wrap muted">
          <a href="/parents">For parents</a> · Unofficial fan project. Not an official Minecraft
          product. Not approved by or associated with Mojang or Microsoft.
        </div>
      </footer>
    `;
  }
}
customElements.define('ka-app', KaApp);
